import React from 'react';

/**
 * Arvexis Logo System v4.0
 * Based on approved mockup - gold "A" with nested inner "A" and crescent moon
 * Color: Flat gold #D4AF37, no gradients, transparent background
 */

const ARVEXIS_GOLD = '#D4AF37';

// Gold A with Nested A and Moon Icon - used in Settings avatar and Home header
export const ArvexisFlowerIcon = ({ size = 24, color = ARVEXIS_GOLD, className = '' }) => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Outer A */}
      <path
        d="M50 5 L12 95 L22 95 L50 30 L78 95 L88 95 L50 5 Z"
        fill={color}
      />
      
      {/* Inner A */}
      <path
        d="M50 28 L32 75 L40 75 L50 50 L60 75 L68 75 L50 28 Z"
        fill={color}
      />
      
      {/* Crossbar */}
      <path
        d="M28 75 L72 75 L68 82 L32 82 Z"
        fill={color}
      />
      
      {/* Crescent Moon */}
      <path
        d="M56 42 A8 8 0 1 1 56 58 A6 6 0 1 0 56 42"
        fill={color}
      />
    </svg>
  );
};

// Full Logo with Wordmark - for Home header
export const ArvexisLogoFull = ({ logoSize = 24, className = '' }) => (
  <div className={`flex items-center gap-2 ${className}`}>
    <ArvexisFlowerIcon size={logoSize} />
    <span 
      style={{
        fontSize: '14px',
        fontWeight: 600,
        letterSpacing: '3px',
        color: ARVEXIS_GOLD,
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
      }}
    >
      ARVEXIS
    </span>
  </div>
);

// 4-pointed Sparkle Icon - used for various accents
export const SparkleIcon = ({ size = 18, color = ARVEXIS_GOLD, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12 0L14.5 9.5L24 12L14.5 14.5L12 24L9.5 14.5L0 12L9.5 9.5L12 0Z" />
  </svg>
);

// Diamond Sparkle - 4 small diamonds (used in View Insights quick action)
export const DiamondSparkle = ({ size = 28, color = '#2DD4BF', className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 28 28"
    fill="none"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M14 2L16 7L14 12L12 7L14 2Z" fill={color} />
    <path d="M26 14L21 16L16 14L21 12L26 14Z" fill={color} />
    <path d="M14 26L12 21L14 16L16 21L14 26Z" fill={color} />
    <path d="M2 14L7 12L12 14L7 16L2 14Z" fill={color} />
  </svg>
);

// Leaf/Sprout Icon - for Growth theme and streak ring
export const LeafIcon = ({ size = 24, color = ARVEXIS_GOLD, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12 22V12" />
    <path d="M12 12C12 12 4 10 4 4C10 4 12 12 12 12Z" />
    <path d="M12 12C12 12 20 10 20 4C14 4 12 12 12 12Z" />
  </svg>
);

// Feather Icon - for New Reflection quick action
export const FeatherIcon = ({ size = 24, color = '#A78BFA', className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5l6.74-6.76z" />
    <line x1="16" y1="8" x2="2" y2="22" />
    <line x1="17.5" y1="15" x2="9" y2="15" />
  </svg>
);

// Heart Outline Icon - for Wellness Check-in
export const HeartOutlineIcon = ({ size = 24, color = '#EC4899', className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

// Moon with Star Icon - for Insights pattern card
export const MoonStarIcon = ({ size = 28, color = 'rgba(200,180,255,0.90)', className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 28 28"
    fill="none"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M19 14C19 18.4183 15.4183 22 11 22C8.79086 22 6.79086 21.1046 5.34315 19.6569C6.52152 20.5185 7.96863 21 9.5 21C13.6421 21 17 17.6421 17 13.5C17 10.567 15.2457 8.04348 12.7143 6.85714C13.4571 6.31429 14.4 6 15.4286 6C17.4038 6 19 7.59619 19 9.57143V14Z"
      fill={color}
    />
    <path
      d="M21 8L21.5 9.5L23 10L21.5 10.5L21 12L20.5 10.5L19 10L20.5 9.5L21 8Z"
      fill={color}
    />
  </svg>
);

export default {
  ArvexisFlowerIcon,
  ArvexisLogoFull,
  SparkleIcon,
  DiamondSparkle,
  LeafIcon,
  FeatherIcon,
  HeartOutlineIcon,
  MoonStarIcon
};
